import { Injectable, inject } from '@angular/core';
import { AbstractControl, ValidatorFn, Validators, ValidationErrors } from '@angular/forms';
import { FormFieldValidator } from '../../models/form-field.model';
import { I18nService } from './i18n.service';

@Injectable({ providedIn: 'root' })
export class FormValidationService {
  private readonly i18n = inject(I18nService);

  private readonly PATTERNS = {
    VIETNAMESE_PHONE: /(84|0[3|5|7|8|9])+([0-9]{8})\b/,
    GLOBAL_PHONE: /^$|^0\d{9,10}$/,
    VIETNAMESE_ID_CARD: /^[0-9]{9}$|^[0-9]{12}$/,
    PASSPORT_NUMBER: /^[A-Z0-9]{6,9}$/,
    INSURANCE_NUMBER: /^[A-Z]{2}\s[1-5]\s\d{2}\s\d{10}$/,
    ALPHANUMERIC: /^[a-zA-Z0-9]+$/,
    NO_SPECIAL_CHARS: /^[a-zA-Z0-9_]+$/,
    NUMERIC: /^[0-9]+$/,
    CURRENCY: /^\d+(\.\d{1,2})?$/,
    PASSWORD: {
      UPPER: /[A-Z]/,
      LOWER: /[a-z]/,
      NUMBER: /[0-9]/,
      SPECIAL: /[!@#$%^&*(),.?":{}|<>]/,
    },
  };

  private readonly VALIDATORS = {
    required: () => Validators.required,
    email: () => Validators.email,
    minLength: (value: number) => Validators.minLength(value),
    maxLength: (value: number) => Validators.maxLength(value),
    min: (value: number) => Validators.min(value),
    max: (value: number) => Validators.max(value),
    pattern: (value: RegExp) => Validators.pattern(value),
    vietnamesePhone: () => this.patternValidatorHasSpace(this.PATTERNS.VIETNAMESE_PHONE, 'vietnamesePhone'),
    globalPhone: () => this.patternValidatorHasSpace(this.PATTERNS.GLOBAL_PHONE, 'globalPhone'),
    vietnameseIdCard: () => this.patternValidator(this.PATTERNS.VIETNAMESE_ID_CARD, 'vietnameseIdCard'),
    passportNumber: () => this.patternValidator(this.PATTERNS.PASSPORT_NUMBER, 'passportNumber'),
    insuranceNumber: () => this.patternValidator(this.PATTERNS.INSURANCE_NUMBER, 'insuranceNumber'),
    alphanumeric: () => this.patternValidator(this.PATTERNS.ALPHANUMERIC, 'alphanumeric'),
    numeric: () => this.patternValidatorHasSpace(this.PATTERNS.NUMERIC, 'numeric'),
    currency: () => this.patternValidator(this.PATTERNS.CURRENCY, 'currency'),
    matchPassword: () => this.matchPassword(),
    passwordStrength: (value: number) => this.passwordStrength(value || 8),
    whitespace: () => this.noWhitespace(),
    url: () => this.url(),
    futureDate: () => this.futureDate(),
    pastDate: () => this.pastDate(),
    dateRange: (value: any) => this.dateRange(value?.minDate, value?.maxDate),
    fileSize: (value: number) => this.fileSize(value),
    fileType: (value: string[]) => this.fileType(value),
    custom: (value: any) => this.createCustomValidator(value),
    noSpecialChars: () => this.patternValidator(this.PATTERNS.NO_SPECIAL_CHARS, 'noSpecialChars'),
  };

  getValidator(config: FormFieldValidator): ValidatorFn | null {
    const { type, value } = config;
    const validatorFn = this.VALIDATORS[type as keyof typeof this.VALIDATORS];
    return validatorFn ? validatorFn(value) : null;
  }

  getValidators(configs?: FormFieldValidator[]): ValidatorFn[] {
    if (!configs?.length) return [];
    return configs.map((c) => this.getValidator(c)).filter((v): v is ValidatorFn => v !== null);
  }

  getErrorMessage(control: AbstractControl, label: string): string {
    if (!control.errors) return '';

    const [errorKey, error] = Object.entries(control.errors)[0];
    
    if (error?.message) {
      return /^[a-z]+\./.test(error.message) 
        ? this.i18n.translate(error.message, { field: label })
        : error.message;
    }

    const params = {
      field: label,
      value: error?.requiredLength ?? error?.min ?? error?.max
    };

    const keyMap: Record<string, string> = {
      required: 'validators.required',
      email: 'validators.email', 
      minlength: 'validators.minLength',
      maxlength: 'validators.maxLength',
      min: 'validators.min',
      max: 'validators.max',
      pattern: 'validators.pattern',
    };

    return this.i18n.translate(keyMap[errorKey] || 'validators.invalid', params);
  }

  getErrorMessageKey(
    errors: ValidationErrors | null,
    fieldName?: string
  ): string | { key: string; params?: any } | null {
    if (!errors) return null;

    const [errorKey, error] = Object.entries(errors)[0];
    if (error?.messageKey) return error.messageKey;

    const errorHandlers = {
      required: () => fieldName 
        ? { key: 'validators.required', params: { field: fieldName } }
        : 'validators.requiredField',
      minlength: () => ({
        key: 'validators.minLength',
        params: { field: fieldName || 'Field', value: error.requiredLength }
      }),
      maxlength: () => ({
        key: 'validators.maxLength', 
        params: { field: fieldName || 'Field', value: error.requiredLength }
      }),
      min: () => ({ key: 'validators.min', params: { value: error.min } }),
      max: () => ({ key: 'validators.max', params: { value: error.max } }),
      email: () => 'validators.email',
      pattern: () => fieldName
        ? { key: 'validators.pattern', params: { field: fieldName } }
        : 'validators.pattern',
    };

    const handler = errorHandlers[errorKey as keyof typeof errorHandlers];
    return handler ? handler() : fieldName
      ? { key: 'validators.invalid', params: { field: fieldName } }
      : 'validators.invalid';
  }

  matchFields(fieldName: string, errorMsg?: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent || !control.value) return null;
      const field = control.parent.get(fieldName);
      if (!field) return null;
      const message = errorMsg || this.i18n.translate('validators.matchFields');
      return control.value === field.value ? null : { matchFields: { message } };
    };
  }

  private patternValidator(pattern: RegExp, errorKey: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const valid = pattern.test(control.value.toString());
      return valid ? null : { [errorKey]: { message: this.i18n.translate(`validators.${errorKey}`) } };
    };
  }

  private patternValidatorHasSpace(pattern: RegExp, errorKey: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === null || control.value === undefined) return null;
  
      const rawValue = control.value.toString();
  
      if (/^\s+/.test(rawValue)) {
        return {
          [errorKey]: {
            message: this.i18n.translate(`validators.${errorKey}`)
          }
        };
      }
  
      // ✅ cho phép space ở CUỐI → trim trước khi test
      const valueToValidate = rawValue.trimEnd();
  
      const valid = pattern.test(valueToValidate);
      return valid
        ? null
        : {
            [errorKey]: {
              message: this.i18n.translate(`validators.${errorKey}`)
            }
          };
    };
  }

  private matchPassword(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent?.value || !control.value) return null;
      const password = control.parent.get('password');
      return control.value === password?.value 
        ? null 
        : { matchPassword: { message: this.i18n.translate('validators.matchPassword') } };
    };
  }

  private passwordStrength(minLength = 8): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const { UPPER, LOWER, NUMBER, SPECIAL } = this.PATTERNS.PASSWORD;
      const val = control.value;
      const valid = val.length >= minLength && [UPPER, LOWER, NUMBER, SPECIAL].every(p => p.test(val));
      return valid ? null : {
        passwordStrength: { message: this.i18n.translate('validators.passwordStrength', { value: minLength }) }
      };
    };
  }

  private noWhitespace(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      return control.value.trim().length === 0
        ? { whitespace: { message: this.i18n.translate('validators.whitespace') } }
        : null;
    };
  }

  private url(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      try {
        new URL(control.value);
        return null;
      } catch {
        return { url: { message: this.i18n.translate('validators.url') } };
      }
    };
  }

  private futureDate(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const date = new Date(control.value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date >= today ? null : { futureDate: { message: this.i18n.translate('validators.futureDate') } };
    };
  }

  private pastDate(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const date = new Date(control.value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date <= today ? null : { pastDate: { message: this.i18n.translate('validators.pastDate') } };
    };
  }

  private dateRange(minDate?: Date, maxDate?: Date): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const date = new Date(control.value);

      if (minDate && date < minDate) {
        return { dateRange: { message: this.i18n.translate('validators.dateRangeAfter', { value: minDate.toLocaleDateString() }) } };
      }
      if (maxDate && date > maxDate) {
        return { dateRange: { message: this.i18n.translate('validators.dateRangeBefore', { value: maxDate.toLocaleDateString() }) } };
      }
      return null;
    };
  }

  private fileSize(maxMB: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const file = control.value instanceof File ? control.value : null;
      if (!file) return null;
      const maxBytes = maxMB * 1024 * 1024;
      return file.size <= maxBytes ? null : { fileSize: { message: this.i18n.translate('validators.fileSize', { value: maxMB }) } };
    };
  }

  private fileType(allowedTypes: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const file = control.value instanceof File ? control.value : null;
      if (!file) return null;
      const ext = file.name.split('.').pop()?.toLowerCase();
      return ext && allowedTypes.includes(ext) ? null : {
        fileType: { message: this.i18n.translate('validators.fileType', { value: allowedTypes.join(', ') }) }
      };
    };
  }

  private createCustomValidator(value: any): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (typeof value === 'function') {
        try {
          const result = value(control.value, control);
          return result ? { custom: { message: result } } : null;
        } catch (error) {
          console.error('[FormValidationService] Custom validator error:', error);
          return null;
        }
      }

      if (value?.validatorFn && typeof value.validatorFn === 'string') {
        try {
          const validatorFn = new Function('control', 'params', value.validatorFn);
          const isValid = validatorFn(control, value.params);
          return isValid ? null : { custom: { message: value.errorMessage || 'Invalid value' } };
        } catch (error) {
          console.error('[FormValidationService] Custom validator error:', error);
          return null;
        }
      }

      return null;
    };
  }
}
